import React, {useEffect, useMemo, useState} from "react";
import {NavLink} from "react-router-dom";
import {css} from "@emotion/css";
import {styled, theme, Text, Icon} from "@reside/ui";

import {menuSectionPath} from "../reporting-paths";
import {MenuItem as ReportingMenuItem} from "./helpers";
import {MenuItemEllipsis} from "./ReportingWorkflowGroup";

const DASHBOARDS_URL_COMPONENT = "dashboards";

type Props = Readonly<{
  subCategories: ReadonlyArray<ReportingMenuItem>;
  categoryUrl: string;
  pathname: string;
  renderGate: (
    menuItem: ReportingMenuItem,
    children: React.ReactNode,
  ) => React.ReactNode;
}>;

export const ReportingAdvancedAnalyticsGroups = ({
  subCategories,
  categoryUrl,
  pathname,
  renderGate,
}: Props) => {
  const activeGroupKey = useMemo(
    () => findActiveAnalyticsGroupKey(pathname, subCategories),
    [pathname, subCategories],
  );

  const defaultOpenKey = activeGroupKey ?? subCategories[0]?.urlComponent;
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(defaultOpenKey ? [defaultOpenKey] : []),
  );

  useEffect(() => {
    if (activeGroupKey) {
      setOpenGroups(prev =>
        prev.has(activeGroupKey) ? prev : new Set(prev).add(activeGroupKey),
      );
    }
  }, [activeGroupKey]);

  const toggleGroup = (groupKey: string) =>
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });

  return (
    <>
      {subCategories.map(group =>
        renderGate(
          group,
          <Accordion key={group.urlComponent}>
            <AccordionHeader
              type="button"
              aria-expanded={openGroups.has(group.urlComponent)}
              onClick={() => toggleGroup(group.urlComponent)}
            >
              <Text
                fontSize={3}
                fontWeight="bold"
                lineHeight="20px"
                color={
                  activeGroupKey === group.urlComponent
                    ? "primary"
                    : "darkBlue100"
                }
              >
                {group.label}
              </Text>
              <Chevron
                name={openGroups.has(group.urlComponent) ? "down" : "right"}
                size={10}
              />
            </AccordionHeader>

            {openGroups.has(group.urlComponent) && (
              <AccordionBody>
                {(group.children ?? []).map(section =>
                  renderGate(
                    section,
                    <MenuItemEllipsis
                      key={section.urlComponent}
                      active={isAnalyticsDashboardActive(
                        pathname,
                        group.urlComponent,
                        section.urlComponent,
                      )}
                    >
                      <NavLink
                        className={({isActive}) =>
                          css`
                            color: inherit;
                            text-decoration: none;
                            ${isActive && `color: ${theme.color.primary};`}
                          `
                        }
                        to={menuSectionPath(
                          categoryUrl,
                          group.urlComponent,
                          section.urlComponent,
                        )}
                        title={section.label}
                      >
                        {section.label}
                      </NavLink>
                    </MenuItemEllipsis>,
                  ),
                )}
              </AccordionBody>
            )}
          </Accordion>,
        ),
      )}
    </>
  );
};

const ANALYTICS_DASHBOARD_PATH_PATTERN =
  /(?:^|\/)analytics\/dashboards\/([^/]+)/;

export const isAnalyticsDashboardActive = (
  pathname: string,
  groupUrlComponent: string,
  sectionId: string,
): boolean => {
  if (groupUrlComponent !== DASHBOARDS_URL_COMPONENT) {
    return false;
  }

  const match = ANALYTICS_DASHBOARD_PATH_PATTERN.exec(pathname);

  return match?.[1] === sectionId;
};

const findActiveAnalyticsGroupKey = (
  pathname: string,
  subCategories: ReadonlyArray<ReportingMenuItem>,
): string | undefined => {
  if (!pathname.includes("/analytics/")) {
    return undefined;
  }

  const dashboardsGroup = subCategories.find(
    group => group.urlComponent === DASHBOARDS_URL_COMPONENT,
  );

  if (!dashboardsGroup) {
    return undefined;
  }

  const match = ANALYTICS_DASHBOARD_PATH_PATTERN.exec(pathname);

  if (!match) {
    return pathname.includes(`/${DASHBOARDS_URL_COMPONENT}`)
      ? DASHBOARDS_URL_COMPONENT
      : undefined;
  }

  const isKnownDashboard = (dashboardsGroup.children ?? []).some(
    section => section.urlComponent === match[1],
  );

  return isKnownDashboard ? DASHBOARDS_URL_COMPONENT : undefined;
};

const Accordion = styled.div`
  & + & {
    margin-top: 6px;
  }
`;

const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    opacity: 0.7;
  }
`;

const AccordionBody = styled.div`
  margin-top: 2px;
`;

const Chevron = styled(Icon)`
  flex-shrink: 0;
  color: ${({theme}) => theme.color.gray100};
`;
